import React, { useEffect, useState, useCallback } from "react";
import debounce from "lodash.debounce";
import api from "../../../Services/api";
import Swal from "sweetalert2";
import { FiSearch, FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { LuGitCompareArrows } from "react-icons/lu";
import "./associacaoQuestaoModal.css";

const AssociacaoQuestaoModal = ({ secaoId, onClose, onAssociarSuccess }) => {
    const [questoes, setQuestoes] = useState([]);
    const [total, setTotal] = useState(0);
    const [secao, setSecao] = useState(null);

    const [filter, setFilter] = useState("");
    const [debouncedFilter, setDebouncedFilter] = useState("");
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(null);

    const limit = 15;

    const calcularProximaOrdem = (lista = []) => {
        const usadas = new Set();
        let max = 0;
        lista.forEach((item) => {
            const ordem = Number(item?.ordem);
            if (Number.isFinite(ordem) && ordem > 0) {
                usadas.add(ordem);
                if (ordem > max) max = ordem;
            }
        });
        for (let i = 1; i <= max + 1; i += 1) {
            if (!usadas.has(i)) return i;
        }
        return max + 1;
    };

    const handleDebounce = useCallback(
        debounce((value) => {
            setDebouncedFilter(value);
            setPage(1);
        }, 400),
        []
    );

    const onChangeFiltro = (e) => {
        setFilter(e.target.value);
        handleDebounce(e.target.value);
    };

    const fetchQuestions = async () => {
        try {
            const response = await api.get("/chkquestoesstats", {
                params: { page, limit, questaoModelo: 1, secaoID: secaoId, filter: debouncedFilter },
            });

            const lista = response.data.data.map((q) => ({
                ...q,
                jaVinculada: q.IDSecao === secaoId,
            }));

            setQuestoes(lista);
            setTotal(response.data.total);
        } catch (err) {
            console.error("Erro ao carregar questões", err);
            Swal.fire("Erro", "Não foi possível carregar as questões.", "error");
        }
    };

    useEffect(() => {
        fetchQuestions();
    }, [page, debouncedFilter]);

    useEffect(() => {
        if (!secaoId) return null;

        const fetchSecao = async () => {
            try {
                const response = await api.get(`/chksecao/${secaoId}`);
                return response.data;
            } catch (error) {
                console.error("Erro ao carregar seção", error);
                return null;
            }
        };
        fetchSecao().then((dados) => {
            setSecao(dados);
        });
    }, [secaoId]);

    const associar = async () => {
        if (!selected) return;

        Swal.fire({
            title: "Associando...",
            text: "Por favor aguarde",
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            let ordem = 1;
            try {
                const responseTotal = await api.get(`/chkquestaosecao/secao`, {
                    params: { page: 1, limit: 1, IDSecao: secaoId, filter: "" },
                });
                const total = responseTotal.data?.total ?? 0;
                let lista = responseTotal.data?.data ?? [];
                if (total > lista.length) {
                    const responseAll = await api.get(`/chkquestaosecao/secao`, {
                        params: { page: 1, limit: total, IDSecao: secaoId, filter: "" },
                    });
                    lista = responseAll.data?.data ?? [];
                }
                ordem = calcularProximaOrdem(lista);
            } catch (err) {
                console.error("Erro ao calcular ordem da associação", err);
            }

            await api.post("/chkquestaosecao", {
                IDSecao: secaoId,
                IDQuestao: selected,
                ordem,
            });

            Swal.fire("Sucesso!", "Questão associada com sucesso.", "success");

            onAssociarSuccess();
            onClose();

        } catch (err) {
            console.error("Erro ao associar", err);
            Swal.fire("Erro", "Não foi possível associar a questão.", "error");
        }
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="associacao-overlay">
            <div className="associacao-modal">

                <div className="modal-header">
                    <h3 className="associacao-title">
                        <LuGitCompareArrows />
                        Associar questão à seção
                    </h3>
                    <button className="close-btn" onClick={onClose}>
                        <FiX />
                    </button>
                </div>

                {secao && (
                    <div className="secaoTitle-field">
                        <label>Seção:</label>
                        <input
                            type="text"
                            name="secao"
                            value={secao.descricao}
                            disabled
                        />
                    </div>)
                }

                <div className="modal-search">
                    <FiSearch className="icon" />
                    <input
                        type="text"
                        placeholder="Buscar questão..."
                        value={filter}
                        onChange={onChangeFiltro}
                    />
                </div>

                <div className="lista-questoes">
                    {questoes.length === 0 ? (
                        <p className="nenhum">Nenhuma questãasso encontrada</p>
                    ) : (
                        questoes.map((q) => (
                            <div
                                key={q.IDQuestao}
                                className={`questao-card ${q.jaVinculada ? "disabled" : ""} ${selected === q.IDQuestao ? "selected" : ""}`}
                                onClick={() => !q.jaVinculada && setSelected(q.IDQuestao)}
                            >
                                <div className="questao-conteudo">
                                    <div className="questao-titulo">#{q.IDQuestao} - {q.titulo}</div>
                                    <div className="questao-descricao">{q.descricao}</div>

                                    {q.jaAssociada && (
                                        <span className="badge">Já vinculada</span>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="pagination">
                    <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                        <FiChevronLeft size={18} style={{ marginRight: 4 }} />
                    </button>

                    <span>Página {page} de {totalPages}</span>

                    <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                        <FiChevronRight size={18} style={{ marginLeft: 4 }} />
                    </button>
                </div>

                <div className="modal-footer">
                    <button className="cancel-btn" onClick={onClose}>
                        Cancelar
                    </button>

                    <button
                        className="confirm-btn"
                        disabled={!selected}
                        onClick={associar}
                    >
                        Associar
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AssociacaoQuestaoModal;
